import type { Maybe } from '@adornis/base/utilTypes.js';
import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import type { ValidationFunction } from '@adornis/validation/validator.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html, nothing } from 'lit';
import {
  Abiturprufungen,
  Altersteilzeit,
  AltersteilzeitAktiv,
  AltersteilzeitPassiv,
  Beschaeftigungsverbot,
  DienstAmAnderenOrt,
  DienstlicheBeurlaubung,
  Elternzeit,
  ElternzeitTeilzeit,
  Exkursion,
  Fortbildung,
  FreieStudien,
  InternerEinsatz,
  KindKrank,
  Klassenfahrt,
  KrankMitAU,
  KrankOhneAU,
  Lehrprobe,
  Mutterschutz,
  Reha,
  Sabbatical,
  Sonderurlaub,
  Sozialpraktikum,
  UnbezahlterUrlaub,
  Urlaub,
  VolleErwerbsminderungsRente,
  VollFreigestelltPflege,
  type Abwesenheit,
} from '../../../db/abwesenheit.js';
import { PartOfDayEnum } from '../../../db/enums/abwesenheiten.js';
import { EntityClass } from '../../../db/enums/class-names.js';
import { capitalizeFirstLetter } from '../../client-utils.js';
import { SgSnackbar } from '../ui/sg-snackbar.js';

/** Selectable Terminarten, in the order they are offered in the dropdown. */
const ABWESENHEITEN = [
  EntityClass.URLAUB,
  // EntityClass.KRANKHEIT,
  EntityClass.KRANKHEIT_OHNE_AU,
  EntityClass.KRANKHEIT_MIT_AU,
  EntityClass.KIND_KRANK,
  EntityClass.MUTTERSCHUTZ,
  EntityClass.ELTERNZEIT,
  EntityClass.ELTERNZEIT_TEILZEIT,
  EntityClass.SABBATICAL,
  // ! Das ist duplette von aktiv und passiv, aber wir wissen bei alten daten nicht ob aktiv oder passiv
  EntityClass.ALTERSTEILZEIT,
  EntityClass.ALTERSTEILZEIT_AKTIV,
  EntityClass.ALTERSTEILZEIT_PASSIV,
  EntityClass.UNBEZAHLTER_URLAUB,
  EntityClass.BESCHAEFTIGUNGSVERBOT,
  EntityClass.FORTBILDUNG,
  EntityClass.DIENSTLICHE_BEURLAUBUNG,
  EntityClass.KLASSENFAHRT,
  EntityClass.EXKURSION,
  EntityClass.DIENST_AM_ANDEREN_ORT,
  EntityClass.LEHRPROBE,
  EntityClass.INTERNER_EINSATZ,
  EntityClass.SOZIALPRAKTIKUM,
  EntityClass.FREIE_STUDIEN,
  EntityClass.ABITURPRUFUNGEN,
  EntityClass.VOLL_FREIGESTELLT_PFLEGE,
  EntityClass.VOLLE_ERWERBSMINDERUNGS_RENTE,
  EntityClass.SONDERURLAUB,
  EntityClass.REHA,
] as const;

type AbwesenheitsArt = (typeof ABWESENHEITEN)[number];

/**
 * Stable, mutable copy for the dropdown. The dropdown keeps its item list in a `useState` keyed on
 * the array identity, so handing it a freshly spread array on every render would reset it constantly.
 */
const ABWESENHEIT_SELECTABLES: AbwesenheitsArt[] = [...ABWESENHEITEN];

const PART_OF_THE_DAY: PartOfDayEnum[] = [PartOfDayEnum.VORMITTAG, PartOfDayEnum.NACHMITTAG];

/**
 * Terminarten die nicht für zukünftige Tage erfasst werden dürfen - man kann sich nicht im Voraus
 * krank melden. Die Auswahl wird zurückgesetzt, sobald ein Startdatum in der Zukunft gewählt wird.
 *
 * ! `Krankheit` ist aktuell nicht auswählbar (siehe {@link ABWESENHEITEN}), die Regel greift also
 * ! derzeit nicht. Sie wird unverändert aus der Altimplementierung übernommen; ob die Nachfolgetypen
 * ! `KrankheitOhneAU`/`KrankheitMitAU` ebenfalls gesperrt werden sollen, ist eine fachliche Frage.
 */
const NICHT_IN_ZUKUNFT_ERFASSBAR: readonly EntityClass[] = [EntityClass.KRANKHEIT];

/** The data the concrete Abwesenheit entities are constructed from. */
interface AbwesenheitCreationDoc {
  personID: string;
  start: Date;
  ende: Date;
  ganztaegig: boolean;
  partOfTheDay: Maybe<PartOfDayEnum>;
  kommentar: Maybe<string>;
}

/**
 * Every selectable Terminart maps to the entity it creates. Typed as a total record so a new entry in
 * {@link ABWESENHEITEN} fails to compile until its entity is registered here - the previous switch
 * statement silently fell through to an abstract `Abwesenheit` instead.
 */
const ABWESENHEIT_BY_ART: Record<AbwesenheitsArt, (doc: AbwesenheitCreationDoc) => Abwesenheit> = {
  [EntityClass.URLAUB]: doc => new Urlaub(doc),
  [EntityClass.KRANKHEIT_OHNE_AU]: doc => new KrankOhneAU(doc),
  [EntityClass.KRANKHEIT_MIT_AU]: doc => new KrankMitAU(doc),
  [EntityClass.KIND_KRANK]: doc => new KindKrank(doc),
  [EntityClass.MUTTERSCHUTZ]: doc => new Mutterschutz(doc),
  [EntityClass.ELTERNZEIT]: doc => new Elternzeit(doc),
  [EntityClass.ELTERNZEIT_TEILZEIT]: doc => new ElternzeitTeilzeit(doc),
  [EntityClass.SABBATICAL]: doc => new Sabbatical(doc),
  [EntityClass.ALTERSTEILZEIT]: doc => new Altersteilzeit(doc),
  [EntityClass.ALTERSTEILZEIT_AKTIV]: doc => new AltersteilzeitAktiv(doc),
  [EntityClass.ALTERSTEILZEIT_PASSIV]: doc => new AltersteilzeitPassiv(doc),
  [EntityClass.UNBEZAHLTER_URLAUB]: doc => new UnbezahlterUrlaub(doc),
  [EntityClass.BESCHAEFTIGUNGSVERBOT]: doc => new Beschaeftigungsverbot(doc),
  [EntityClass.FORTBILDUNG]: doc => new Fortbildung(doc),
  [EntityClass.DIENSTLICHE_BEURLAUBUNG]: doc => new DienstlicheBeurlaubung(doc),
  [EntityClass.KLASSENFAHRT]: doc => new Klassenfahrt(doc),
  [EntityClass.EXKURSION]: doc => new Exkursion(doc),
  [EntityClass.DIENST_AM_ANDEREN_ORT]: doc => new DienstAmAnderenOrt(doc),
  [EntityClass.LEHRPROBE]: doc => new Lehrprobe(doc),
  [EntityClass.INTERNER_EINSATZ]: doc => new InternerEinsatz(doc),
  [EntityClass.SOZIALPRAKTIKUM]: doc => new Sozialpraktikum(doc),
  [EntityClass.FREIE_STUDIEN]: doc => new FreieStudien(doc),
  [EntityClass.ABITURPRUFUNGEN]: doc => new Abiturprufungen(doc),
  [EntityClass.VOLL_FREIGESTELLT_PFLEGE]: doc => new VollFreigestelltPflege(doc),
  [EntityClass.VOLLE_ERWERBSMINDERUNGS_RENTE]: doc => new VolleErwerbsminderungsRente(doc),
  [EntityClass.SONDERURLAUB]: doc => new Sonderurlaub(doc),
  [EntityClass.REHA]: doc => new Reha(doc),
};

const translateArt = (art: AbwesenheitsArt): string => String(clientTranslator.translate('_class', art));

/**
 * The dialog edits days, not instants: `start` and `ende` are both inclusive calendar days, stored
 * canonically as 00:00:00.000Z of the day meant.
 */
interface AbwesenheitFormData {
  art: Maybe<AbwesenheitsArt>;
  start: Maybe<Date>;
  ende: Maybe<Date>;
  ganztaegig: boolean;
  partOfTheDay: Maybe<PartOfDayEnum>;
  kommentar: Maybe<string>;
}

const pflichtfeld =
  (label: string): ValidationFunction<unknown, AbwesenheitFormData> =>
  options => {
    if (options.value === undefined || options.value === null || options.value === '')
      throw new ValidationError(`Feld "${label}" muss ausgefüllt werden!`, { key: options.key });
  };

/** Both ends are inclusive, so a one day absence has `start === ende` and is valid. */
const startVorEnde: ValidationFunction<Maybe<Date>, AbwesenheitFormData> = options => {
  const ende = options.target.ende;
  if (!options.value || !ende) return;
  if (options.value.getTime() > ende.getTime())
    throw new ValidationError('Das Startdatum muss vorm Enddatum liegen!', { key: options.key });
};

const formatValidationErrors = (error: ValidationError | MultipleValidationError): string => {
  const messages =
    error instanceof MultipleValidationError ? error.validationErrors.map(e => e.message) : [error.message];
  return `Ein oder mehrere Felder wurden nicht korrekt ausgefüllt: ${messages.join(' ')}`;
};

export interface AbwesenheitBeantragenDialogProps {
  personID: string;
  /** Preselected first included day. Normalized to a canonical calendar day. */
  start?: Maybe<Date>;
  /** Preselected last included day. Normalized to a canonical calendar day. */
  ende?: Maybe<Date>;
  ganztaegig?: boolean;
  /** When true the dialog does not close on backdrop click or escape, only via its close button. */
  modal?: boolean;
}

const AbwesenheitBeantragenDialogContent = stateful(
  (
    props: AbwesenheitBeantragenDialogProps & {
      resolve: (newAbwesenheitID: string) => void;
      reject: () => void;
    },
  ) => {
    // callers may hand us raw instants (e.g. a fullcalendar selection), so normalize to canonical days
    const [initialFormData] = useState<AbwesenheitFormData>({
      art: null,
      start: toCalendarDay(props.start),
      ende: toCalendarDay(props.ende),
      ganztaegig: props.ganztaegig ?? false,
      partOfTheDay: PartOfDayEnum.VORMITTAG,
      kommentar: null,
    });

    const form = useForm<AbwesenheitFormData>(initialFormData, {
      validator: {
        start: and(pflichtfeld('Start'), startVorEnde),
        ende: pflichtfeld('Ende'),
      },
    });
    const document = form.document;

    const artField = form.field('art');
    const startField = form.field('start');
    const endeField = form.field('ende');

    const handleCreate = async () => {
      const art = document.art;
      if (!art) {
        void SgSnackbar.show('Bitte geben Sie eine Terminart an!');
        return;
      }
      if (!(await confirmDialog('Termin eintragen?'))) return;

      const validationError = await form.validate();
      if (validationError) {
        void SgSnackbar.showError(formatValidationErrors(validationError));
        return;
      }

      const start = document.start;
      const ende = document.ende;
      // already covered by the validation above, this only narrows the types
      if (!start || !ende) return;

      const abwesenheit = ABWESENHEIT_BY_ART[art]({
        personID: props.personID,
        start,
        ende,
        ganztaegig: document.ganztaegig,
        partOfTheDay: document.partOfTheDay,
        kommentar: document.kommentar,
      });

      await guardDialog(() => abwesenheit.create(), {
        success: newAbwesenheitID => {
          void SgSnackbar.show(`${translateArt(art)} wurde erfolgreich erstellt.`);
          props.resolve(newAbwesenheitID);
        },
      });
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: 'Termin eintragen',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Art</span>
          ${DropdownSelectionInput({
            ...artField,
            selectables: ABWESENHEIT_SELECTABLES,
            renderString: translateArt,
            placeholder: 'Art',
            placeholderMode: 'hide',
            clearable: true,
          })}
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Zeitraum</span>
          <div class="flex flex-col gap-2 md:flex-row">
            ${DateInput({
              ...startField,
              type: 'date',
              placeholder: 'Start',
              onPicked: (picked: Date | null) => {
                const day = toCalendarDay(picked);
                startField.onPicked?.(day);

                // eine Krankmeldung für einen zukünftigen Tag ist nicht möglich
                if (
                  day &&
                  document.art &&
                  NICHT_IN_ZUKUNFT_ERFASSBAR.includes(document.art) &&
                  day.getTime() > todayAsCalendarDay().getTime()
                )
                  artField.onPicked?.(null);
              },
            })}
            ${DateInput({
              ...endeField,
              type: 'date',
              placeholder: 'Ende',
              onPicked: (picked: Date | null) => endeField.onPicked?.(toCalendarDay(picked)),
            })}
          </div>
          ${
            document.start && document.ende
              ? html`
                  <p class="text-sm text-base-content/70">
                    ${formatCalendarDay(document.start, 'dd.MM.yyyy')} bis einschließlich
                    ${formatCalendarDay(document.ende, 'dd.MM.yyyy')}
                  </p>
                `
              : nothing
          }
        </div>

        ${ToggleInput({ ...form.field('ganztaegig'), label: 'Ganztägig' })}
        ${
          !document.ganztaegig
            ? html`
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-base-content/70">Terminzeitraum</span>
                  ${DropdownSelectionInput({
                    ...form.field('partOfTheDay'),
                    selectables: PART_OF_THE_DAY,
                    renderString: capitalizeFirstLetter,
                    placeholder: 'Terminzeitraum',
                    placeholderMode: 'hide',
                    clearable: true,
                  })}
                </div>
              `
            : nothing
        }

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Kommentar</span>
          ${TextInput({
            ...form.field('kommentar'),
            placeholder: 'Kein Kommentar hinterlegt',
            placeholderMode: 'hide',
            clearable: true,
          })}
        </div>

        <div class="flex flex-row justify-end">
          <button class="btn btn-primary" @click=${handleCreate}>Termin eintragen</button>
        </div>
      </div>
    `;
  },
);

/**
 * Opens the "Termin eintragen" dialog for a single person.
 *
 * `start` and `ende` are inclusive calendar days; whatever the caller passes is normalized to the
 * canonical day encoding before it reaches the form.
 *
 * @returns the ID of the created Abwesenheit, or null if the user dismissed the dialog.
 */
export async function showAbwesenheitBeantragenDialog(props: AbwesenheitBeantragenDialogProps): Promise<string | null> {
  try {
    return await showDialog<string>({
      modal: props.modal,
      modalBoxClasses: 'max-w-2xl',
      content: (resolve, reject) => AbwesenheitBeantragenDialogContent({ ...props, resolve, reject }),
    });
  } catch {
    // dialog was dismissed
    return null;
  }
}
